import { ALL, UNAVAILABLE_DAYS, WeekdaysMap } from "lib/constants";
import {
  areDatesEqual,
  getAllDatesBetween,
  getLocalISO,
  sortDates,
} from "lib/utils/date-utils";
import { DateTime } from "luxon";

class RoomBookingService {
  getBookedRoomDates = (
    subscriptions: PractitionerSubscriptionDetails[],
    roomId: string
  ): Date[] => {
    console.log(roomId);
    // Default all rooms
    if (roomId?.localeCompare(ALL) === 0) {
      const value = subscriptions
        .flatMap((subscription) => subscription.dates)
        .map((dateString) => new Date(dateString));

      return value;
    }

    const value = subscriptions
      .filter(
        (subscription) =>
          subscription.roomId === roomId && !subscription.isCancelled
      )
      .flatMap((subscription) => subscription.dates)
      .map((dateString) => new Date(dateString));

    return value;
  };

  getUnavailableRoomDates = (
    subscriptions: PractitionerSubscriptionDetails[],
    roomId: string
  ): UnavailableDay[] =>
    this.getBookedRoomDates(subscriptions, roomId).map((date) => {
      return {
        date: getLocalISO(date),
        internalDate: date,
        type: UNAVAILABLE_DAYS.HOLIDAY,
        title: "Unavailable for booking",
      } as UnavailableDay;
    });

  getPractitionerRoomBookings = (
    subscriptions: PractitionerSubscriptionDetails[],
    practitionerId: string
  ): PractitionerSubscriptionDetails[] =>
    subscriptions.filter(
      (subscription) => subscription.practitionerId === practitionerId
    );

  getAvailableRooms = (
    subscriptions: PractitionerSubscriptionDetails[],
    clinicRooms: RoomDetails[],
    practitionerId: string
  ): RoomDetails[] => {
    const bookedRoomIds = new Set(
      subscriptions
        .filter((subscription) =>
          subscription.practitionerId.localeCompare(practitionerId)
        )
        .map((subscription) => subscription.roomId)
    );
    return clinicRooms.filter((room) => !bookedRoomIds.has(room.id));
  };

  getNextAvailableDate = (date: Date, unavailableDates: Date[]): Date => {
    if (
      unavailableDates.some((unavailableDate) =>
        areDatesEqual(date, unavailableDate)
      )
    ) {
      const nextDate = DateTime.fromJSDate(date).plus({ days: 7 }).toJSDate();
      return this.getNextAvailableDate(nextDate, unavailableDates);
    }
    return date;
  };

  getNextAvailableDates = (
    startDate: Date,
    unavailableDates: Date[],
    weekDays: number[]
  ): Date[] => {
    return (weekDays ?? []).flatMap((dayNumber) => {
      const startDateNumber = startDate.getDay() - dayNumber;

      let nextAvailableDate = new Date(startDate);
      if (startDateNumber > 0) {
        const nextStartDate = DateTime.fromJSDate(startDate)
          .plus({ days: Math.abs(startDateNumber) })
          .toJSDate();

        nextAvailableDate = this.getNextAvailableDate(
          nextStartDate,
          unavailableDates
        );
      } else if (startDateNumber < 0) {
        const nextStartDate = DateTime.fromJSDate(startDate)
          .plus({ days: Math.abs(startDateNumber) })
          .toJSDate();

        nextAvailableDate = this.getNextAvailableDate(
          nextStartDate,
          unavailableDates
        );
      } else {
        // equals 0
        nextAvailableDate = this.getNextAvailableDate(
          startDate,
          unavailableDates
        );
      }

      return nextAvailableDate;
    });
  };

  getSelectableDates = (
    startDate: Date,
    unavailableDates: Date[],
    weekdays: string[],
    months: number
  ): Date[] => {
    if (
      !startDate ||
      // unavailableDates?.length === 0 ||
      weekdays?.length === 0 ||
      !months
    ) {
      return [];
    }

    const weekDaysMap = Object.entries(WeekdaysMap);
    const weekDayNumbers = weekdays.map((day) =>
      weekDaysMap.findIndex(([_, value]) => value === day)
    );

    let availableStartDates = this.getNextAvailableDates(
      startDate,
      unavailableDates,
      weekDayNumbers
    );

    availableStartDates = sortDates(availableStartDates);

    const nextAvailableDate = availableStartDates?.[0] ?? startDate;

    const endDate = DateTime.fromJSDate(nextAvailableDate)
      .plus({ months: months })
      .toJSDate();

    const dates = getAllDatesBetween(nextAvailableDate, endDate, weekdays);

    const returnValue = dates.filter(
      (date) =>
        !unavailableDates.some((unavailableDate) =>
          areDatesEqual(date, unavailableDate)
        )
    );

    return returnValue;
  };
}

const ROOM_BOOKING_SERVICE = new RoomBookingService();
export default ROOM_BOOKING_SERVICE;
